import { computed, makeObservable } from 'mobx';
import { getDisplayGeoJSONFeature } from '../LayersTree/Tree/utils';
import { ConnectedLayer } from './ConnectedLayer';
import { RecordAttachments } from './RecordAttachments';
import { TableColumn } from './TableColumn';
import { TableInfo } from './TableInfo';
import { Record } from './Record';
import {
  CardOptions,
  CardInfo,
  CardField,
  CardFile,
  RecordCardServices,
} from './types';

const DEFAULT_OPTIONS: CardOptions = {
  excludedColumn: [],
  findConnectedLayer: false,
};

export class RecordCard {
  readonly connectedLayer: ConnectedLayer;
  readonly tableInfo: TableInfo;
  readonly tableColumns: TableColumn;
  readonly record: Record;
  readonly attachments: RecordAttachments;
  private _options: CardOptions;

  constructor(
    services: RecordCardServices,
    tableName: string,
    recordId: string | number,
    options?: Partial<CardOptions>,
  ) {
    this._options = { ...DEFAULT_OPTIONS, ...options };
    this.tableInfo = new TableInfo(tableName, services.table);
    this.tableColumns = new TableColumn(tableName, services.table);
    this.record = new Record(tableName, recordId, services.records);
    this.attachments = new RecordAttachments(
      tableName,
      recordId,
      services.attachment,
    );
    this.connectedLayer = new ConnectedLayer(
      tableName,
      this._options,
      services.map,
    );
    makeObservable(this);
  }

  @computed get tableName() {
    return this.tableInfo.tableName;
  }

  @computed get recordId() {
    return this.record.recordId;
  }

  @computed get cardInfo(): CardInfo {
    const display = this._getDisplayValue();
    const table = this.tableInfo.description || this.tableInfo.tableName;

    return {
      partition: table,
      name: display || '' + this.record.recordId,
    };
  }

  @computed get fields(): CardField[] {
    return this._createFields();
  }

  @computed get files(): CardFile[] {
    return this._createFileList();
  }

  load = async () => {
    await Promise.all([
      this.tableInfo.load(),
      this.tableColumns.load(),
      this.record.load(),
      this.attachments.load(),
      this.connectedLayer.load(),
    ]);
  };

  private _createFields() {
    const fields: CardField[] = [];

    for (let column of this.tableColumns.getColumnIterator()) {
      const { column_name, description, data_type, references } = column;

      if (this._options.excludedColumn.includes(column_name)) continue;

      let value = this.record.getProperty(column_name);

      if (column_name === 'geom') value = this.record.geometry;

      if (column_name === 'id') value = this.record.recordId;

      fields.push({
        columnName: column_name,
        columnDescription: description || column_name,
        value,
        type: data_type,
        references,
      });
    }

    return fields;
  }

  private _createFileList() {
    const files: CardFile[] = [];

    for (let attachment of this.attachments.attachments) {
      const { id, fileinfo, file } = attachment;

      if (!id || !fileinfo || !file) continue;

      const { filename, mimetype } = fileinfo;

      if (!filename) continue;

      files.push({
        attachmentId: id,
        fileName: filename,
        mimetype,
        file,
      });
    }

    return files;
  }

  private _getDisplayValue() {
    const { record } = this.record;
    const { display } = this.tableInfo;

    if (!record || !display) return;

    return getDisplayGeoJSONFeature(record, display);
  }
}
